import "../style.scss";

import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import { Avatar } from "@mui/material";
import ActionAddPhotoCamera from "assets/img/svg/actionAddPhotoCamera";
import classNames from "classnames";
import StyledButton from "components/shared/StyledButton";
import { ITransfereeFormValues } from "containers/TransfereesFormContainer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { uploadPicture } from "store/documentStore";
import { useAppDispatch } from "store/store";
import type { TFetchStatus } from "store/types";

interface ImageFormComponentProps {
    label: string;
    avatar: string | undefined;
    setAvatar: any;
    sendPictureStatus: TFetchStatus;
    control: any;
    controlName: string;
    /**
     * The type define the css image , if it's a square or a portrait etc
     */
    type?: "publication" | "beneficiary" | "title" | "new-beneficiary" | "brand";
    disabled?: boolean;
}

const ImageFormComponent = (props: ImageFormComponentProps) => {
    const { label, avatar, setAvatar, sendPictureStatus, control, controlName, type, disabled = false } = props;
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const [value, setValue] = useState(null);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const firstRender = useRef(true);

    const imageSrc = useMemo(() => {
        if (value && typeof value === "object") {
            return URL.createObjectURL(value[0]);
        }
        return value;
    }, [value]);

    useEffect(() => {
        const fetchAvatar = async (fileList: FileList) => {
            dispatch(uploadPicture(fileList[0])).then((res) => {
                if (res.meta.requestStatus === "fulfilled") {
                    setAvatar("avatarUrl", res.payload as string);
                }
                if (res.meta.requestStatus === "rejected") {
                    setAvatar("avatarUrl", "");
                }
            });
        };
        if (typeof avatar !== "string") {
            fetchAvatar(avatar as unknown as any);
        }
    }, [avatar]);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        } else if (avatar === "" && inputRef.current) {
            inputRef.current.value = "";
            // inputRef.current.click();
        }
    }, [avatar]);

    const renderAvatar = useCallback(() => {
        if (imageSrc) {
            return (
                <Avatar
                    variant={type == "publication" || type == "title" ? "rounded" : "circular"}
                    src={imageSrc}
                    alt="img"
                    // imgProps={{ sx: { objectFit: "contain" } }}
                    sx={uploadBoxStyle(type)}
                />
            );
        }

        return (
            <>
                <label
                    className={classNames({
                        "upload-box": true,
                        "image-form-label": true,
                        "image-form-label-disabled": disabled,
                    })}
                    htmlFor="raised-button-file"
                    style={uploadBoxStyle(type)}
                >
                    <ActionAddPhotoCamera />
                </label>
                <div className="input-control" style={{ display: "unset" }}>
                    <StyledButton
                        itemType="label"
                        onClick={() => inputRef.current?.click()}
                        buttonType="secondary"
                        style={{
                            borderColor: "black",
                            padding: "0px 16px",
                            width: "unset",
                        }}
                        disabled={disabled}
                    >
                        {t("Upload.AddPicture")}
                    </StyledButton>
                    <label htmlFor="raised-button-file" style={{ display: "block", marginBottom: "8px" }}>
                        <span style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                            {/* {t("Upload.Image")} */}
                            <span style={{ fontWeight: "600" }}> JPG </span>
                            {t("Upload.Or")}
                            <span style={{ fontWeight: "600" }}> PNG </span>
                            (max. <span style={{ fontWeight: "600" }}>5{t("Upload.Mb")}</span>)
                        </span>
                    </label>
                </div>
            </>
        );
    }, [imageSrc, t]);

    return (
        <div className="form-component">
            <Controller
                name={controlName}
                control={control}
                render={({ field }) => {
                    setValue(field.value);
                    return (
                        <div className="input-group-form">
                            <label className="form-component-label" htmlFor={controlName}>
                                {label}
                            </label>
                            <div className="upload-image-element">
                                <input
                                    ref={inputRef}
                                    onChange={(event) => field.onChange(event.target.files)}
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    id="raised-button-file"
                                    type="file"
                                />
                                {renderAvatar()}
                                {avatar !== "" && (
                                    <LoadingButton
                                        loading={sendPictureStatus === "pending"}
                                        onClick={() => setAvatar("avatarUrl", "")}
                                        disabled={disabled}
                                        sx={{
                                            height: "fit-content",
                                            color: "black",
                                        }}
                                    >
                                        {sendPictureStatus !== "pending" && <RefreshIcon sx={{ color: "black" }} />}
                                    </LoadingButton>
                                )}
                            </div>
                        </div>
                    );
                }}
            />
        </div>
    );
};

export default ImageFormComponent;

/** Dynamic styles */
const uploadBoxStyle = (type?: "publication" | "beneficiary" | "title" | "new-beneficiary" | "brand") => {
    const styles = {
        publication: {
            width: "100px",
            height: "auto",
            aspectRatio: "3/4",
            borderRadius: "3px",
        },
        beneficiary: { width: "100px", height: "100px", borderRadius: "100%" },
        title: { width: "100px", aspectRatio: "3/4", borderRadius: "3px" },
        "new-beneficiary": {
            width: "150px",
            aspectRatio: "1/1",
            height: "auto",
            borderRadius: "3px",
        },
        brand: { width: "80px", height: "80px", borderRadius: "3px" },
    };
    const defaultStyle = { width: "80px", height: "80px", borderRadius: "3px" };

    if (type) return styles[type];
    else return defaultStyle;
    // return styles[type] || defaultStyle as React.CSSProperties;
};
